import React, { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { Hooks, Util } from "@xt/xtbase";
import DatePicker from "../../../react-mobile-datepicker";
import { get_convertOrderList } from "@/api/v4/convert";
import styles from "./index.module.less";

const { useTranslation } = Hooks;
const { moment } = Util;

/** 格式化为 YYYY-MM-DD */
function formatDateStr(date) {
  if (!date) return "";
  const d = moment(date);
  const y = d.year();
  const m = String(d.month() + 1).padStart(2, "0");
  const day = String(d.date()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** 接口 state -> 展示 status：processing | completed | failed */
function mapStateToStatus(state) {
  const s = (state || "").toUpperCase();
  if (s === "PENDING") return "processing";
  if (s === "SUCCEED") return "completed";
  if (s === "FAILED") return "failed";
  return "processing";
}

/** 接口单条转为列表展示：优先 sourceCurrencyCode / targetCurrencyCode（v2 from/to） */
function mapOrderToItem(order) {
  const sourceAmount = order.sourceAmount ?? "";
  const targetAmount = order.targetAmount ?? "";
  const sourceCurrencyCode = order.sourceCurrencyCode || "";
  const targetCurrencyCode = order.targetCurrencyCode || "";
  const fromAmount = `${sourceAmount} ${sourceCurrencyCode}`.trim();
  const toAmount = `${targetAmount} ${targetCurrencyCode}`.trim();
  const created = order.createdTime || "";
  const timeStr = created ? moment(created).format("YYYY-MM-DD HH:mm:ss") : "";
  return {
    id: String(order.id),
    fromAmount,
    toAmount,
    time: timeStr,
    status: mapStateToStatus(order.state),
  };
}

const RANGE_DAYS = 90;
const DEFAULT_PAGE_SIZE = 20;

export default function ConvertHistory() {
  const t = useTranslation();
  const today = useMemo(() => formatDateStr(new Date()), []);
  const defaultFrom = useMemo(() => formatDateStr(moment().subtract(RANGE_DAYS, "days")), []);
  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(today);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);

  const hasMore = totalCount > 0 && list.length < totalCount;
  const canLoadMore = hasMore && !loading && !loadingMore;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerValue, setPickerValue] = useState(null);
  const [pickerMin, setPickerMin] = useState(null);
  const [pickerMax, setPickerMax] = useState(null);
  const pickerTypeRef = useRef("start");

  const dateFromLabel = useMemo(() => dateFrom || "", [dateFrom]);
  const dateToLabel = useMemo(() => dateTo || "", [dateTo]);

  const handleClosePicker = useCallback(() => {
    setPickerOpen(false);
  }, []);

  const handleSelectPicker = useCallback((date) => {
    const str = formatDateStr(date);
    if (pickerTypeRef.current === "start") {
      setDateFrom(str);
    } else {
      setDateTo(str);
    }
    setPickerOpen(false);
  }, []);

  const handleClickFrom = useCallback(() => {
    pickerTypeRef.current = "start";
    const fromDate = dateFrom ? moment(dateFrom).toDate() : new Date();
    const toDate = dateTo ? moment(dateTo).endOf("day").valueOf() : moment().endOf("day").valueOf();
    const todayEnd = moment().endOf("day").valueOf();
    const max = Math.min(toDate, todayEnd);
    const min = moment(max).subtract(RANGE_DAYS, "days").startOf("day").valueOf();
    setPickerValue(fromDate);
    setPickerMin(new Date(min));
    setPickerMax(new Date(max));
    setPickerOpen(true);
  }, [dateFrom, dateTo]);

  const handleClickTo = useCallback(() => {
    pickerTypeRef.current = "end";
    const toDate = dateTo ? moment(dateTo).toDate() : new Date();
    const fromTs = dateFrom ? moment(dateFrom).startOf("day").valueOf() : moment().subtract(RANGE_DAYS, "days").startOf("day").valueOf();
    const endTs = moment(fromTs).add(RANGE_DAYS, "days").endOf("day").valueOf();
    const todayEnd = moment().endOf("day").valueOf();
    const max = Math.min(endTs, todayEnd);
    setPickerValue(toDate);
    setPickerMin(new Date(fromTs));
    setPickerMax(new Date(max));
    setPickerOpen(true);
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    setPageIndex(1);
    const startTime = `${dateFrom}T00:00:00`;
    const endTime = `${dateTo}T23:59:59`;
    get_convertOrderList({
      startTime,
      endTime,
      pageIndex: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    })
      .then((res) => {
        const result = res?.result ?? res?.data ?? res;
        const items = result?.items ?? [];
        const total = result?.totalCount ?? 0;
        setList(items.map(mapOrderToItem));
        setTotalCount(total);
      })
      .catch(() => {
        setList([]);
        setTotalCount(0);
      })
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  const loadMore = useCallback(() => {
    if (!dateFrom || !dateTo || !canLoadMore) return;
    const nextPage = pageIndex + 1;
    setLoadingMore(true);
    const startTime = `${dateFrom}T00:00:00`;
    const endTime = `${dateTo}T23:59:59`;
    get_convertOrderList({
      startTime,
      endTime,
      pageIndex: nextPage,
      pageSize: DEFAULT_PAGE_SIZE,
    })
      .then((res) => {
        const result = res?.result ?? res?.data ?? res;
        const items = result?.items ?? [];
        setList((prev) => [...prev, ...items.map(mapOrderToItem)]);
        setPageIndex(nextPage);
      })
      .finally(() => setLoadingMore(false));
  }, [dateFrom, dateTo, pageIndex, canLoadMore]);

  useEffect(() => {
    if (!canLoadMore || !loadMoreRef.current) return;
    const el = loadMoreRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root: null, rootMargin: "100px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [canLoadMore, loadMore]);

  return (
    <div className={styles["convert-history"]}>
      <div className={styles["convert-history__date-row"]}>
        <button
          type="button"
          className={styles["convert-history__date-btn"]}
          onClick={handleClickFrom}
        >
          {dateFromLabel}
        </button>
        <span className={styles["convert-history__date-to"]}>{t("convert.dateTo")}</span>
        <button
          type="button"
          className={styles["convert-history__date-btn"]}
          onClick={handleClickTo}
        >
          {dateToLabel}
        </button>
      </div>
      {pickerOpen && (
        <DatePicker
          open={pickerOpen}
          value={pickerValue}
          min={pickerMin}
          max={pickerMax}
          onClose={handleClosePicker}
          onSelect={handleSelectPicker}
        />
      )}

      <div className={styles["convert-history__list-section"]}>
        <div className={styles["convert-history__list-header"]}>
          <span>{t("convert.spend")}</span>
          <span>{t("convert.receive")}</span>
        </div>

        {loading ? (
          <div className={styles["convert-history__empty"]}>{t("convert.loading")}...</div>
        ) : list.length === 0 ? (
          <div className={styles["convert-history__empty"]}>{t("convert.noHistory")}</div>
        ) : (
          <div className={styles["convert-history__list"]}>
            {list.map((item) => (
              <div key={item.id} className={styles["convert-history__item-wrap"]}>
                <div className={styles["convert-history__item"]}>
                  <div className={styles["convert-history__item-left"]}>
                    <div className={`${styles["convert-history__item-from"]} ${item.status === "failed" ? styles["convert-history__item--muted"] : ""}`.trim()}>{item.fromAmount}</div>
                    <div className={styles["convert-history__item-time"]}>{item.time}</div>
                  </div>
                  <div className={styles["convert-history__item-right"]}>
                    <div className={`${styles["convert-history__item-to"]} ${item.status === "failed" ? styles["convert-history__item--muted"] : ""}`.trim()}>{item.toAmount}</div>
                    <div className={styles["convert-history__item-status"]}>{t(item.status === "processing" ? "convert.processing" : item.status === "failed" ? "convert.failed" : "convert.completed")}</div>
                  </div>
                </div>
                <div className={styles["convert-history__divider"]} />
              </div>
            ))}
            {canLoadMore && <div ref={loadMoreRef} className={styles["convert-history__load-more"]} aria-hidden="true" />}
            {loadingMore && <div className={styles["convert-history__loading-more"]}>{t("convert.loading")}...</div>}
          </div>
        )}
      </div>
    </div>
  );
}
